import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Badge, Button, Icon, type IconName } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";

export const TOUR_STORAGE_KEY = "nelurio.tour.v1";

interface CatalogSection {
  icon: IconName;
  tone: "purple" | "blue" | "green" | "amber" | "coral";
  titleKey: string;
  bodyKey: string;
}

/**
 * Section-by-section help catalog. Replaces the old spotlight tour: one popup,
 * every app section explained in full — what it does and what the user can do
 * inside it. Fully translated via the shared i18n catalogs (10 languages).
 */
// Titles = the REAL tab names (nav keys): a newcomer connects every tour entry
// to the tab it explains, in every locale, forever in sync.
const sections: CatalogSection[] = [
  { icon: "home", tone: "purple", titleKey: "nav.dashboard", bodyKey: "tour.s1.body" },
  { icon: "book", tone: "blue", titleKey: "nav.courses", bodyKey: "tour.s2.body" },
  { icon: "library", tone: "green", titleKey: "nav.library", bodyKey: "tour.s3.body" },
  { icon: "sparkle", tone: "purple", titleKey: "nav.assistant", bodyKey: "tour.s4.body" },
  { icon: "calendar", tone: "coral", titleKey: "nav.planner", bodyKey: "tour.s5.body" },
  { icon: "check", tone: "amber", titleKey: "nav.tasks", bodyKey: "tour.s6.body" },
  { icon: "tree", tone: "green", titleKey: "nav.focus", bodyKey: "tour.s7.body" },
  { icon: "chart", tone: "blue", titleKey: "nav.progress", bodyKey: "tour.s8.body" },
  { icon: "route", tone: "purple", titleKey: "nav.academicPath", bodyKey: "tour.s9.body" },
  { icon: "settings", tone: "amber", titleKey: "nav.more", bodyKey: "tour.s10.body" }
];

export function OnboardingTour({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const [expanded, setExpanded] = useState<number | null>(null);

  const finish = React.useCallback(() => {
    window.localStorage.setItem(TOUR_STORAGE_KEY, new Date().toISOString());
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setExpanded(null);
    // Any close path (X, backdrop, Escape) counts as "seen" so the catalog
    // never nags an existing user again.
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") finish(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish]);

  if (!open) return null;

  return createPortal(
    <div className="composer-backdrop" onClick={finish} role="presentation">
      <div className="composer-modal catalog rise" role="dialog" aria-modal="true" aria-label={t("tour.title")} onClick={(event) => event.stopPropagation()}>
        <div className="catalog__head">
          <span className="tour-card__brand">N</span>
          <div>
            <h2>{t("tour.title")}</h2>
            <p>{t("tour.catalogLead")}</p>
          </div>
          <button className="composer-modal__close" onClick={finish} aria-label={t("tour.skip")}><Icon name="x" size={16} /></button>
        </div>

        <div className="catalog__list">
          {sections.map((section, index) => {
            const isOpen = expanded === index;
            return (
              <article key={section.titleKey} className={`catalog__item ${isOpen ? "is-open" : ""}`}>
                <button className="catalog__row" onClick={() => setExpanded(isOpen ? null : index)} aria-expanded={isOpen}>
                  <span className={`catalog__icon tone-${section.tone}`}><Icon name={section.icon} size={16} /></span>
                  <strong>{t(section.titleKey)}</strong>
                  <Icon name="chevron" size={14} className={isOpen ? "catalog__chev is-open" : "catalog__chev"} />
                </button>
                {isOpen && <p className="catalog__body">{t(section.bodyKey)}</p>}
              </article>
            );
          })}
        </div>

        <div className="composer-modal__actions">
          <Badge tone="purple">{formatNumber(sections.length)} {t("tour.sections")}</Badge>
          <Button icon="check" onClick={finish}>{t("tour.done")}</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
